import React, { useEffect, useState } from 'react';
import ApexCharts from 'react-apexcharts';

const InvestmentProgress = ({ 
  plan, 
  investedAmount, 
  startDate, 
  maturityDate, 
  expectedReturn, 
  lockInPeriod, 
  profitPercentage 
}) => {
  const [chartData, setChartData] = useState([]);
  const [progress, setProgress] = useState(0);
  const [isMatured, setIsMatured] = useState(false);

  // Generate daily progress data from start date to maturity date
  const generateProgressData = () => {
    const start = new Date(startDate);
    const maturity = new Date(maturityDate);
    const today = new Date();
    const totalDuration = maturity - start;
    const totalDays = Math.ceil(totalDuration / (1000 * 60 * 60 * 24));
    
    const progressData = [];
    
    // Calculate how many days have elapsed or should be shown
    const currentDate = today > maturity ? maturity : today;
    const daysToShow = Math.ceil((currentDate - start) / (1000 * 60 * 60 * 24));
    
    // Generate data points for each day
    for (let day = 0; day <= Math.min(daysToShow, totalDays); day++) {
      const currentDay = new Date(start);
      currentDay.setDate(start.getDate() + day);
      
      let dayValue;
      if (day === 0) {
        // Starting value
        dayValue = investedAmount;
      } else if (day >= totalDays) {
        // Final maturity value
        dayValue = expectedReturn;
      } else {
        // Progressive growth calculation
        const dayProgress = day / totalDays;
        const profitAmount = expectedReturn - investedAmount;
        
        // Use a slight random variation within the min-max profit range for realistic growth
        const minProfit = (investedAmount * profitPercentage.min * dayProgress) / 100;
        const maxProfit = (investedAmount * profitPercentage.max * dayProgress) / 100;
        const avgProfit = (minProfit + maxProfit) / 2;
        
        // Add some realistic daily fluctuation (±2% of daily growth)
        const fluctuation = (Math.random() - 0.5) * 0.04 * avgProfit;
        dayValue = investedAmount + avgProfit + fluctuation;
        
        // Ensure the value doesn't go below invested amount
        dayValue = Math.max(dayValue, investedAmount);
      }
      
      progressData.push({
        x: currentDay.getTime(),
        y: Math.round(dayValue * 100) / 100 // Round to 2 decimal places
      });
    }
    
    return progressData;
  };

  // Calculate current progress
  const calculateCurrentProgress = () => {
    const start = new Date(startDate);
    const maturity = new Date(maturityDate);
    const today = new Date();
    
    const totalDuration = maturity - start;
    const timeElapsed = today - start;
    
    if (today >= maturity) {
      setProgress(100);
      setIsMatured(true);
    } else if (today <= start) {
      setProgress(0);
      setIsMatured(false);
    } else {
      const progressPercentage = Math.min((timeElapsed / totalDuration) * 100, 100);
      setProgress(progressPercentage);
      setIsMatured(false);
    }
  };

  useEffect(() => {
    calculateCurrentProgress();
    const data = generateProgressData();
    setChartData(data);

    // Update progress every hour for active investments
    const interval = setInterval(() => {
      if (!isMatured) {
        calculateCurrentProgress();
        const updatedData = generateProgressData();
        setChartData(updatedData);
      }
    }, 1000 * 60 * 60); // Update every hour

    return () => clearInterval(interval);
  }, [startDate, maturityDate, expectedReturn, investedAmount, isMatured]);

  const chartOptions = {
    chart: {
      type: 'area',
      height: 200,
      sparkline: { enabled: false },
      toolbar: { show: false },
      zoom: { enabled: false }
    },
    stroke: {
      width: 2,
      curve: 'smooth'
    },
    colors: [isMatured ? '#28a745' : '#007bff'],
    fill: {
      type: 'gradient',
      gradient: {
        shade: 'light',
        type: 'vertical',
        shadeIntensity: 0.25,
        gradientToColors: [isMatured ? '#20c997' : '#17a2b8'],
        inverseColors: false,
        opacityFrom: 0.6,
        opacityTo: 0.1,
        stops: [0, 100]
      }
    },
    grid: {
      show: true,
      strokeDashArray: 3,
      borderColor: '#e0e0e0',
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } }
    },
    tooltip: {
      enabled: true,
      theme: 'light',
      x: {
        format: 'dd MMM yyyy'
      },
      y: {
        formatter: (val) => `$${val.toFixed(2)}`,
        title: {
          formatter: () => 'Value: '
        }
      }
    },
    xaxis: {
      type: 'datetime',
      labels: {
        format: 'dd/MM',
        style: {
          fontSize: '10px',
          colors: '#666'
        }
      },
      axisBorder: { show: false },
      axisTicks: { show: false }
    },
    yaxis: {
      labels: {
        formatter: (val) => `$${val.toFixed(0)}`,
        style: {
          fontSize: '10px',
          colors: '#666'
        }
      },
      min: Math.floor(investedAmount * 0.95), // Start slightly below invested amount
      max: Math.ceil(expectedReturn * 1.05) // End slightly above expected return
    },
    annotations: {
      yaxis: [
        {
          y: investedAmount,
          borderColor: '#ff6b6b',
          borderWidth: 1,
          strokeDashArray: 5,
          label: {
            borderColor: '#ff6b6b',
            style: {
              color: '#fff',
              background: '#ff6b6b',
              fontSize: '10px'
            },
            text: `Invested: $${investedAmount}`
          }
        },
        {
          y: expectedReturn,
          borderColor: '#51cf66',
          borderWidth: 1,
          strokeDashArray: 5,
          label: {
            borderColor: '#51cf66',
            style: {
              color: '#fff',
              background: '#51cf66',
              fontSize: '10px'
            },
            text: `Target: $${expectedReturn}`
          }
        }
      ]
    }
  };

  const chartSeries = [
    {
      name: 'Investment Growth',
      data: chartData
    }
  ];

  // Progress bar component
  const ProgressBar = () => (
    <div className="mt-3">
      <div className="d-flex justify-content-between align-items-center mb-1">
        <small className="text-muted">Progress</small>
        <small className={`fw-bold ${isMatured ? 'text-success' : 'text-primary'}`}>
          {progress.toFixed(1)}%
        </small>
      </div>
      <div className="progress" style={{ height: '6px' }}>
        <div
          className={`progress-bar ${isMatured ? 'bg-success' : 'bg-primary'}`}
          role="progressbar"
          style={{ width: `${progress}%` }}
          aria-valuenow={progress}
          aria-valuemin="0"
          aria-valuemax="100"
        ></div>
      </div>
      <div className="d-flex justify-content-between mt-1">
        <small className="text-muted">
          {new Date(startDate).toLocaleDateString()}
        </small>
        <small className="text-muted">
          {new Date(maturityDate).toLocaleDateString()}
        </small>
      </div>
    </div>
  );

  return (
    <div>
      <ApexCharts
        options={chartOptions}
        series={chartSeries}
        type="area"
        height={200}
      />
      <ProgressBar />
      <div className="mt-2">
        <div className="row text-center">
          <div className="col-4">
            <small className="text-muted d-block">Lock Period</small>
            <small className="fw-bold">{lockInPeriod} days</small>
          </div>
          <div className="col-4">
            <small className="text-muted d-block">Profit Range</small>
            <small className="fw-bold text-success">
              {profitPercentage.min}% - {profitPercentage.max}%
            </small>
          </div>
          <div className="col-4">
            <small className="text-muted d-block">Status</small>
            <small className={`fw-bold ${isMatured ? 'text-success' : 'text-primary'}`}>
              {isMatured ? 'Matured' : 'Active'}
            </small>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvestmentProgress;